import { Checkbox, Group, Paper, Stack, Text } from "@mantine/core";
import { useCallback, useMemo, type ChangeEvent } from "react";
import type { ReflexPanelProposal } from "../../services/reflexPanels.service";

interface ReflexPanelProposalOptionProps {
  proposal: ReflexPanelProposal;
  checked: boolean;
  disabled: boolean;
  onToggle: (proposalKey: string, checked: boolean) => void;
  distinguishFromTissueCypher?: boolean;
}

export default function ReflexPanelProposalOption({
  proposal,
  checked,
  disabled,
  onToggle,
  distinguishFromTissueCypher = false,
}: ReflexPanelProposalOptionProps) {
  const componentSummary = useMemo(
    () =>
      proposal.targetPanel.components
        .map((component) => `${component.stainCode} ×${component.quantity}`)
        .join(" · "),
    [proposal.targetPanel.components]
  );
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onToggle(proposal.proposalKey, event.currentTarget.checked);
    },
    [onToggle, proposal.proposalKey]
  );

  return (
    <Paper withBorder p="md" radius="md">
      <Group wrap="nowrap" align="flex-start">
        <Checkbox
          aria-label={`Order ${
            distinguishFromTissueCypher ? "reflex panel " : ""
          }${proposal.targetPanel.name} for specimen ${
            proposal.specimenNumber
          }`}
          checked={checked}
          disabled={disabled}
          onChange={handleChange}
        />
        <Stack gap={3}>
          <Text fw={600}>
            {distinguishFromTissueCypher ? "Reflex panel: " : ""}
            {proposal.targetPanel.name}
          </Text>
          <Text size="sm">
            Specimen {proposal.specimenNumber} · ICD{" "}
            {proposal.matchingIcdCodes.join(", ")}
          </Text>
          <Text size="xs" c="dimmed">
            {componentSummary}
          </Text>
        </Stack>
      </Group>
    </Paper>
  );
}
